import {
  applyPuzzleMove,
  isPuzzleSolved,
  parsePuzzleLevel,
  type PuzzleLevel,
  type PuzzleLevelSource,
  type PuzzleMove,
  type PuzzleState,
} from '@veriverse/game-logic';
import { create } from 'zustand';

/** Which cat the puzzle was opened at; the panel is shown while this is set (spec §6.14.4). */
export const useCatPuzzleStore = create<{
  objectId: number | undefined;
  openAt(objectId: number): void;
  close(): void;
}>((set) => ({
  objectId: undefined,
  openAt: (objectId) => set({ objectId }),
  close: () => set({ objectId: undefined }),
}));

/** One attempt at a level: every position so far (for undo) and the moves that led there. */
export interface PuzzleRun {
  readonly level: PuzzleLevel;
  readonly history: readonly PuzzleState[];
  readonly moves: string;
  readonly solved: boolean;
}

export function startRun(source: PuzzleLevelSource): PuzzleRun {
  const level = parsePuzzleLevel(source);
  return { level, history: [level.start], moves: '', solved: false };
}

export function currentState(run: PuzzleRun): PuzzleState {
  return run.history[run.history.length - 1] ?? run.level.start;
}

/** The run after a move; unchanged when the move is blocked or the level is already solved. */
export function moveRun(run: PuzzleRun, move: PuzzleMove): PuzzleRun {
  if (run.solved) return run;
  const next = applyPuzzleMove(run.level, currentState(run), move);
  if (!next) return run;
  return {
    level: run.level,
    history: [...run.history, next],
    moves: run.moves + move,
    solved: isPuzzleSolved(run.level, next),
  };
}

/** Takes back the last move (undo counts nothing: the score is the length of the final run). */
export function undoRun(run: PuzzleRun): PuzzleRun {
  if (run.solved || run.history.length <= 1) return run;
  return {
    level: run.level,
    history: run.history.slice(0, -1),
    moves: run.moves.slice(0, -1),
    solved: false,
  };
}

export type PuzzleCommand = PuzzleMove | 'undo' | 'restart';

const KEYS: Readonly<Record<string, PuzzleCommand>> = {
  ArrowUp: 'u',
  ArrowDown: 'd',
  ArrowLeft: 'l',
  ArrowRight: 'r',
  w: 'u',
  s: 'd',
  a: 'l',
  d: 'r',
  z: 'undo',
  u: 'undo',
  Backspace: 'undo',
  r: 'restart',
};

/** Keyboard command for a key press: arrows/WASD move, Z/U/Backspace undo, R restarts. */
export function puzzleCommand(
  event: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey'>,
) {
  if (event.altKey || event.metaKey) return undefined;
  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  if (event.ctrlKey) return key === 'z' ? 'undo' : undefined;
  return Object.hasOwn(KEYS, key) ? KEYS[key] : undefined;
}

export interface PuzzleCell {
  readonly tile: 'wall' | 'floor' | 'target' | 'void';
  readonly piece: 'player' | 'cat' | 'prop' | undefined;
}

/** The board as rows of cells for drawing. */
export function boardCells(run: PuzzleRun): PuzzleCell[][] {
  const state = currentState(run);
  const { width, height, tiles } = run.level;
  return Array.from({ length: height }, (_, y) =>
    Array.from({ length: width }, (_, x) => {
      const cell = y * width + x;
      const piece =
        cell === state.player
          ? 'player'
          : cell === state.cat
            ? 'cat'
            : state.props.includes(cell)
              ? 'prop'
              : undefined;
      return { tile: tiles[cell] ?? 'void', piece };
    }),
  );
}
